import axios from "axios";
import { createContext, useEffect, useReducer, useState } from "react";
import { useTenant } from "../hooks";
import { toast } from "react-toastify";

const AccountContext = createContext();

const addAccountRecursive = (nodes, parentId, newAccount) => {
  return nodes.map((node) => {
    if (node.id === parentId) {
      return {
        ...node,
        childs: [...(node.childs || []), newAccount],
      };
    }

    if (node.childs && node.childs.length) {
      return {
        ...node,
        childs: addAccountRecursive(node.childs, parentId, newAccount),
      };
    }

    return node;
  });
};
const editAccountRecursive = (nodes, payload) => {
  return nodes.map((node) => {
    if (node.id === payload.id) {
      return { ...node, ...payload };
    }

    if (node.childs?.length) {
      return {
        ...node,
        childs: editAccountRecursive(node.childs, payload),
      };
    }

    return node;
  });
};

const deleteAccountRecursive = (nodes, id) => {
  return nodes
    .filter((node) => node.id !== id)
    .map((node) => ({
      ...node,
      childs: node.childs ? deleteAccountRecursive(node.childs, id) : [],
    }));
};

const AccountReducer = (state, action) => {
  switch (action.type) {
    case "SET_ACCOUNTS":
      return action.payload;

    case "ADD_ACCOUNT": {
      const { parentId, newAccount } = action.payload;

      if (!parentId) return [...state, newAccount];

      return addAccountRecursive(state, parentId, newAccount);
    }

    case "EDIT_ACCOUNT":
      return editAccountRecursive(state, action.payload);

    case "DELETE_ACCOUNT":
      return deleteAccountRecursive(state, action.payload.id);

    default:
      return state;
  }
};

export const AccountsProvider = ({ children }) => {
  const BaseUrl = process.env.REACT_APP_INV_API_SERVER_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const { tenantId, companySelected } = useTenant();

  const [accounts, dispatch] = useReducer(AccountReducer, []);
  const fetchAccounts = async () => {
    try {
      setLoading(true);

      const response = await axios.post(`${BaseUrl}/tree-account`, {
        tenant_id: tenantId,
        company_id: companySelected?.value,
      });
   
      const accounts = response?.data?.data?.treeParent || [];

      dispatch({
        type: "SET_ACCOUNTS",
        payload: accounts,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId && companySelected?.value) {
      fetchAccounts();
    }
  }, [tenantId, companySelected?.value]);
  // add
  const addAccount = async (payload) => {
    try {
      setLoadingSave(true);

      const response = await axios.post(
        `${BaseUrl}/tree-account/store`,
        payload,
      );

      await fetchAccounts();

      return response;
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingSave(false);
    }
  };
  // edit
  const editAccount = async (payload, id) => {
    try {
      setLoadingSave(true);
      const response = await axios.post(`${BaseUrl}/tree-account/store`, {
        id,
        ...payload,
      });
      const updatedAccount = {
        id,
        ...response?.data?.data,
      };
      dispatch({
        type: "EDIT_ACCOUNT",
        payload: updatedAccount,
      });

      return response;
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingSave(false);
    }
  };

  // delete
  const deleteAccount = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${BaseUrl}/tree-account/delete/${id}`);
      dispatch({
        type: "DELETE_ACCOUNT",
        payload: { id },
      });
      return response;
    } catch (error) {
      console.log(error);
      toast.error(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <AccountContext.Provider
      value={{
        accounts,
        dispatch,
        addAccount,
        loadingSave,
        loading,
        editAccount,
        deleteAccount,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};

export default AccountContext;
